import { Route, Routes } from 'react-router-dom';
import OrdersPage from './pages/OrdersPage/OrdersPage';
import ContractsPage from './pages/ContractsPage/ContractsPage';
import SalesPage from './pages/SalesPage/SalesPage';
import LoginPage from './pages/LoginPage/LoginPage';
import { ProtectedRoute } from './config/PrivateRoute';
import { ModuleRoute } from './config/ModuleRoute';
import CalendarPage from './pages/CalendarPage/CalendarPage';
import ProductionPage from './pages/ProductionPage/ProductionPage';
import SettingsPage from './pages/SettingsPage/SettingsPage';
import MarketingPage from './pages/MarketingPage/MarketingPage';
import LeadsPage from './pages/LeadsPage/LeadsPage';
import CustomersPage from './pages/CustomersPage/CustomersPage';
import TabletPage from './components/TabletPage/TabletPage';
import TermsPage from './pages/TermsPage/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage/PrivacyPolicyPage';
import EmployeesSection from './pages/AdminPage/sections/EmployeesSection/EmployeesSection';
import PartnersSection from './pages/ProductionPage/sections/PartnersSection/PartnersSection';
import StockSection from './pages/ProductionPage/sections/StockSection/StockSection';
import ProvidersSegment from './components/ProvidersSection/ProvidersSection';
import DressesSection from './pages/ProductionPage/sections/DressesSection/DressesSection';
import ProductionOrdersPage from './pages/ProductionOrdersPage/ProductionOrdersPage';
import { useScrollToTop } from './hooks/useScroolToTop';
import OrdersStatisticsPageV3 from './pages/OrdersStatisticsPage/OrdersStatisticsPageV3';
import CommissionContractsStatisticsPage from './pages/CommissionContractsStatisticsPage/CommissionContractsStatisticsPage';
import { Settings } from './store/reducers/AccountSlice';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import CatalogSingleShowRoomPage from './pages/CatalogSingleShowRoomPage/CatalogSingleShowRoomPage';
import BookingsPage from './pages/BookingsPage/BookingsPage';
import OnlineBookingsPage from './pages/OnlineBookingsPage/OnlineBookingsPage';
import { EnrichedPublicRoute } from './config/EnrichedPublicRoute';
import WelcomePage from './pages/WelcomePage/WelcomePage';
import BookingDetailsPage from './pages/BookingDetailsPage/BookingDetailsPage';
import StripeReturn from './pages/SettingsPage/sections/StripeReturn';
import OnboardingBookingFlow from './components/OnboardingBookingFlow/OnboardingBookingFlow';
import OnboardingEntryPage from './pages/OnboardingPage/OnboardingEntryPage';
import TabletStatisticsPage from './pages/TabletStatisticsPage/TabletStatisticsPage';
import FindPartnersPage from "./pages/FindPartnersPage/FindPartnersPage";
import CommissionSettingsPage from './pages/CommissionSettingsPage/CommissionSettingsPage';
import CommissionTrackPage from './pages/CommissionTrackPage/CommissionTrackPage';
import CommissionContractPage from './pages/CommissionContractPage';
import CommissionTrackAllPage from './pages/CommissionTrackAllPage';
import BenefitsPage from './pages/BenefitsPage/BenefitsPage';
import PaymentsPage from './pages/PaymentsPage/PaymentsPage';
import PricingPlansPage from './pages/PricingPlansPage/PricingPlansPage';
import CustomersCrmStatisticsPage from './pages/CustomersCrmStatisticsPage/CustomersCrmStatisticsPage';
import SocialContentPage from './pages/SocialContentPage/SocialContentPage';
import OnlineStorePage from './pages/OnlineStorePage/OnlineStorePage';
import PublicStorePage from './pages/PublicStorePage/PublicStorePage';
import EcommerceStatisticsPage from './pages/EcommerceStatisticsPage/EcommerceStatisticsPage';
import UnifiedStatisticsPage from './pages/UnifiedStatisticsPage/UnifiedStatisticsPage';
import type { TenantModule } from './shared/tenantModules';
import { MaintenanceAPI } from './api/MaintenanceAPI';
import MaintenancePage from './pages/MaintenancePage/MaintenancePage';
import { LoadingScreen } from './components/LoadingScreen/LoadingScreen';

function App() {

  useScrollToTop();
  const settings: Settings = useSelector((state: any) => state.settings.value);
  const brandName = settings.tenantName || 'Unknown Customer';
  const [maintenanceCheck, setMaintenanceCheck] = useState<'loading' | 'off' | 'on'>('loading');
  const [maintenanceContent, setMaintenanceContent] = useState('');

  useEffect(() => {
    if (brandName) {
      datadogRum.setGlobalContextProperty('brandName', brandName);
    }
  }, [brandName]);

  useEffect(() => {
    let isMounted = true;
    MaintenanceAPI.get()
      .then((status) => {
        if (!isMounted) return;
        if (status.enabled) {
          setMaintenanceContent(status.content ?? '');
          setMaintenanceCheck('on');
        } else {
          setMaintenanceCheck('off');
        }
      })
      .catch((error) => {
        console.error('Failed to load maintenance status; continuing normally', { error });
        if (isMounted) {
          setMaintenanceCheck('off');
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const moduleRoute = (module: TenantModule, page: JSX.Element) => (
    <ModuleRoute module={module}>
      {page}
    </ModuleRoute>
  );

  if (maintenanceCheck === 'loading') {
    return <LoadingScreen />;
  }

  if (maintenanceCheck === 'on') {
    return <MaintenancePage content={maintenanceContent} />;
  }

  return (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/terms-of-service" element={<TermsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                {moduleRoute('STORE', <CalendarPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <ProtectedRoute>
                {moduleRoute('STORE', <BookingsPage />)}
              </ProtectedRoute>
            }
          />
          <Route path="/bookings/:bookingWidgetId" element={<OnlineBookingsPage />} />
          <Route path="/bookings/:bookingWidgetId/:reservationId" element={<BookingDetailsPage />} />
          <Route
            path="/store"
            element={
              <ProtectedRoute>
                <OnlineStorePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                {moduleRoute('STORE', <CustomersPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <LeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production"
            element={
              <ProtectedRoute>
                {moduleRoute('PRODUCTION', <ProductionPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/social-content"
            element={
              <ProtectedRoute>
                <SocialContentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tablet"
            element={
              <ProtectedRoute>
                <TabletPage />
              </ProtectedRoute>
            }
          />
          <Route path="/settings/payments/setup/return" element={<ProtectedRoute><StripeReturn /></ProtectedRoute>} />
          <Route path="/catalogues" element={<ProtectedRoute><MarketingPage /></ProtectedRoute>} />
          {/* <Route path="/catalogues/showroom" element={<CatalogShowRoomListingPage />} /> */}
          <Route path="/catalogues/:someCatalogueId" element={
            <EnrichedPublicRoute>
              <CatalogSingleShowRoomPage />
            </EnrichedPublicRoute>
          } />
          <Route path="/catalogues/:someCatalogueId/:itemId" element={
            <EnrichedPublicRoute>
              <CatalogSingleShowRoomPage />
            </EnrichedPublicRoute>
          } />
          <Route path="/store/:storeLink" element={
            <EnrichedPublicRoute>
              <PublicStorePage />
            </EnrichedPublicRoute>
          } />
          <Route path="/store/:storeLink/:dressId" element={
            <EnrichedPublicRoute>
              <PublicStorePage />
            </EnrichedPublicRoute>
          } />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <EmployeesSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                {moduleRoute('PRODUCTION', <DressesSection />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/partners"
            element={
              <ProtectedRoute>
                <PartnersSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock"
            element={
              <ProtectedRoute>
                {moduleRoute('PRODUCTION', <StockSection />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/providers"
            element={
              <ProtectedRoute>
                {moduleRoute('PRODUCTION', <ProvidersSegment />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/production-orders"
            element={
              <ProtectedRoute>
                {moduleRoute('PRODUCTION', <ProductionOrdersPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                {moduleRoute('B2B_ORDERS', <OrdersPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <ProtectedRoute>
                {moduleRoute('STORE', <SalesPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts"
            element={
              <ProtectedRoute>
                <ContractsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/tablet"
            element={
              <ProtectedRoute>
                <TabletStatisticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/orders"
            element={
              <ProtectedRoute>
                {moduleRoute('PRODUCTION', <OrdersStatisticsPageV3 />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/commission-contracts"
            element={
              <ProtectedRoute>
                {moduleRoute('SECOND_HAND', <CommissionContractsStatisticsPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/payments"
            element={
              <ProtectedRoute>
                {moduleRoute('B2B_ORDERS', <PaymentsPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/customers-crm"
            element={
              <ProtectedRoute>
                <CustomersCrmStatisticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/meetings"
            element={
              <ProtectedRoute>
                {moduleRoute('STORE', <UnifiedStatisticsPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/ecommerce"
            element={
              <ProtectedRoute>
                <EcommerceStatisticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={<OnboardingEntryPage />}
          />
          <Route
            path="/onboarding/booking"
            element={
              <ProtectedRoute>
                <OnboardingBookingFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/find-partners"
            element={
              <ProtectedRoute>
                <FindPartnersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/commission/settings"
            element={
              <ProtectedRoute>
                {moduleRoute('SECOND_HAND', <CommissionSettingsPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/drops/track/:trackingId"
            element={<CommissionTrackPage />}
          />
          <Route
            path="/commission/drops"
            element={
              <ProtectedRoute>
                {moduleRoute('SECOND_HAND', <CommissionTrackAllPage />)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/drops/:contractLinkId"
            element={<CommissionContractPage />}
          />
          <Route
            path="/benefits"
            element={
              <ProtectedRoute>
                <BenefitsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <PricingPlansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <WelcomePage />
              </ProtectedRoute>
            }
          />
        </Routes>
  );
}

export default App;
